import _ from 'lodash';
import angular from 'angular';

const DEFAULT_CONTEXT = 'app';

@Inject('$q', 'HttpService', '$sessionStorage')
class RestWrapperService {
  constructor() {
    this.cache = this.$sessionStorage.listCached || {};
    this.devMode = window.devMode;
    if (this.devMode) {
      this.HttpService.put = this.HttpService.post = this.HttpService.delete = this.HttpService.get;
    }
  }

  applyParameters(path, urlParams) {
    if (path && urlParams) {
      let urlParamsToOmit = [];
      const newPath = path.replace(/\{([A-Za-z-]+)\}/g, (_, m) => {
        urlParamsToOmit.push(m);
        return urlParams[m];
      });
      const newUrlParams = _.omit(urlParams, urlParamsToOmit);
      return {
        path: newPath,
        urlParams: newUrlParams,
      };
    }
    return { path, urlParams };
  }

  overwriteHandleError(handleErrorFn) {
    this.HttpService.overwrite('handleError', handleErrorFn);
    return this;
  }

  evalResourceUrl(resourceName, resourceParams) {
    const resourceUrlParams = this.evalResourceUrlParams(resourceParams);
    if (resourceName.includes('?') && resourceUrlParams) {
      return `${resourceName}&${resourceUrlParams}`;
    } else if (!resourceName.includes('?') && resourceUrlParams) {
      return `${resourceName}?${resourceUrlParams}`;
    }
    return resourceName;
  }

  parseParams(params, joinSeparator) {
    const isObjectButNotArray = typeof params === 'object' && !Array.isArray(params);
    let paramsToTrasform = params;

    if (isObjectButNotArray) {
      paramsToTrasform = Object.entries(params);
    }

    return paramsToTrasform
      .map((parameter) => ({
        name: parameter[0],
        value: parameter[1],
      }))
      .filter((item) => item.value !== void 0 && item.value !== null)
      .map((item) => this.evalResourceUrlParam(item.name, item.value))
      .filter((item) => item !== '')
      .join(joinSeparator);
  }

  evalResourceUrlParams(resourceParams) {
    let resourceUrlParams = '';
    if (resourceParams && !this.isEmptyValue(resourceParams)) {
      const filtersObj = this.flattenFilters(resourceParams);
      resourceUrlParams = this.parseParams(filtersObj, '&');
    }

    return resourceUrlParams;
  }

  flattenFilters(resourceParams) {
    let flattenedParams = _.cloneDeep(resourceParams);
    if (flattenedParams) {
      const filters = flattenedParams.filters;
      flattenedParams.filters = null;
      flattenedParams = Object.assign(flattenedParams, filters);
    }
    return flattenedParams;
  }

  evalResourceUrlParam(rawName, rawValue) {
    return Array.isArray(rawValue) ? this.encodeArray(rawName, rawValue) : `${this.evalResourceUrlParamName(rawName)}=${this.evalResourceUrlParamValue(rawValue)}`;
  }

  evalResourceUrlParamName(rawName) {
    return encodeURIComponent(rawName);
  }

  evalResourceUrlParamValue(rawValue) {
    if (_.isObject(rawValue)) {
      return this.parseParams(rawValue, '$');
    }
    return encodeURIComponent(rawValue);
  }

  encodeArray(name, value) {
    return value.length
      ? value
          .reduce((prev, curr) => {
            if (curr !== null && curr !== undefined) {
              const encoded = encodeURIComponent(curr);
              const newPrev = prev.concat(`&${name}=${encoded}`);
              return newPrev;
            }
            return prev;
          }, '')
          .slice(1)
      : '';
  }

  isEmptyValue(rawValue) {
    if (_.isObject(rawValue)) {
      return (
        _.chain(rawValue)
          .map((itemValue, itemKey) => ({ itemValue, itemKey }))
          .filter((item) => !this.isEmptyValue(item.itemValue))
          .value().length === 0
      );
    }
    return !rawValue && rawValue !== 0;
  }

  create(context = DEFAULT_CONTEXT, restUrl, entity = {}, urlParams = {}, headers = {}) {
    if (this.devMode) {
      return this.list(context, restUrl, entity, urlParams);
    }
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, applyParameters.urlParams);
    return this.HttpService.post(context, resourceUrl, entity, headers).then((response) => {
      if (entity) {
        angular.extend(entity, response.data);
      }
      return response.data;
    });
  }

  update(context = DEFAULT_CONTEXT, restUrl, entity = {}, urlParams = {}) {
    if (this.devMode) {
      return this.list(restUrl, entity, urlParams);
    }
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, applyParameters.urlParams);
    return this.HttpService.put(context, resourceUrl, entity).then((response) => _.get(response, 'data'));
  }

  remove(context = DEFAULT_CONTEXT, restUrl, urlParams = {}, config = { data: null }) {
    if (this.devMode) {
      return this.list(context, restUrl, urlParams, config);
    }
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, applyParameters.urlParams);
    return this.HttpService.delete(context, resourceUrl, config);
  }

  get(context = DEFAULT_CONTEXT, restUrl, resourceParams, urlParams = {}, path) {
    // alias for list
    return this.list(context, restUrl, resourceParams, urlParams, path);
  }

  list(context = DEFAULT_CONTEXT, restUrl, resourceParams, urlParams = {}, path) {
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, resourceParams);
    return this.HttpService.get(context, resourceUrl, null, path).then((response) => _.get(response, 'data'));
  }

  listCached(context = DEFAULT_CONTEXT, restUrl, resourceParams, urlParams = {}, force = false, path) {
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, resourceParams);
    const deferred = this.$q.defer();
    const cached = this.cache[resourceUrl];

    if (!force && !!cached) {
      // use cache
      deferred.resolve(cached);
    } else {
      // reload from server
      this.HttpService.get(context, resourceUrl, null, path).then((response) => {
        if (response && response.data) {
          this.cache[resourceUrl] = response.data;
          this.$sessionStorage.listCached = this.cache;
        }
        deferred.resolve(this.cache[resourceUrl]);
      });
    }

    return deferred.promise;
  }

  downloadBlob(context = DEFAULT_CONTEXT, restUrl, resourceParams, urlParams = {}, headers = {}, method = 'GET', entity = {}) {
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, resourceParams);

    return this.HttpService.getFile(
      context,
      resourceUrl,
      {
        responseType: 'blob',
        ignoreLoadingBar: true,
        headers,
      },
      method,
      entity
    );
  }

  downloadBuffer(context = DEFAULT_CONTEXT, restUrl, resourceParams, urlParams = {}, method = 'GET', entity = {}) {
    const applyParameters = this.applyParameters(restUrl, urlParams);
    const resourceUrl = this.evalResourceUrl(applyParameters.path, resourceParams);
    return this.HttpService.getFile(context, resourceUrl, { responseType: 'arraybuffer' }, method, entity);
  }
}

export default RestWrapperService;



// WEBPACK FOOTER //
// ./src/modules/utils/rest/rest-wrapper-service.js